import { motion } from 'motion/react';
import {
  Cloud,
  CloudLightning,
  CloudRain,
  Flame,
  Leaf,
  Mountain,
  Snowflake,
  Sparkles,
  Sun,
  Wind,
  type LucideIcon,
} from 'lucide-react';
import { GlassCard } from '@/components/shared/GlassCard';
import { SectionHeader } from '@/components/shared/SectionHeader';
import { brand } from '@/lib/theme/tokens';
import { fadeUp, inViewOnce, staggerContainer } from '@/lib/motion/variants';
import { useWeather } from '@/features/marketplace/hooks/useWeather';
import { useLocalEvents } from '@/features/marketplace/hooks/useLocalEvents';
import type { WeatherCondition } from '@/features/marketplace/types';

function weatherIcon(icon: WeatherCondition['icon']): LucideIcon {
  switch (icon) {
    case 'sun':   return Sun;
    case 'rain':  return CloudRain;
    case 'snow':  return Snowflake;
    case 'storm': return CloudLightning;
    case 'mist':  return Wind;
    default:      return Cloud;
  }
}

function weatherColor(icon: WeatherCondition['icon']): string {
  switch (icon) {
    case 'sun':   return brand.ok;
    case 'rain':  return brand.ice;
    case 'snow':  return brand.ice;
    case 'storm': return brand.warn;
    case 'mist':  return brand.ice;
    default:      return brand.ok;
  }
}

function eventIcon(name: string): LucideIcon {
  switch (name) {
    case 'flame':     return Flame;
    case 'leaf':      return Leaf;
    case 'snowflake': return Snowflake;
    case 'sun':       return Sun;
    case 'mountain':  return Mountain;
    default:          return Sparkles;
  }
}

function eventColor(token: string): string {
  switch (token) {
    case 'chinar':  return brand.chinar;
    case 'ok':      return brand.ok;
    case 'saffron': return brand.saffron;
    case 'ice':     return brand.ice;
    case 'warn':    return brand.warn;
    default:        return brand.teal;
  }
}

export function ValleyPulse() {
  const { data: weatherData = [], isLoading: weatherLoading } = useWeather();
  const { data: events = [] } = useLocalEvents();

  return (
    <section className="mx-auto mt-24 max-w-7xl px-4 md:px-6">
      <GlassCard className="relative overflow-hidden p-7 md:p-9">
        <div
          aria-hidden
          className="pointer-events-none absolute -right-20 -top-20 h-[360px] w-[360px] rounded-full"
          style={{ background: `radial-gradient(${brand.teal}30, transparent 70%)` }}
        />

        <div className="relative grid grid-cols-1 gap-10 lg:grid-cols-[1.1fr_1fr] lg:items-start">
          {/* Left — live weather */}
          <div>
            <SectionHeader
              kicker="Live · valley pulse"
              title={
                <>
                  What Kashmir{' '}
                  <em className="text-[var(--color-teal)]">looks like right now.</em>
                </>
              }
            />

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={inViewOnce}
              variants={staggerContainer}
              className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2"
            >
              {weatherLoading
                ? Array.from({ length: 4 }).map((_, i) => (
                    <div
                      key={i}
                      className="animate-pulse rounded-xl border border-[var(--glass-edge)] glass p-3.5 h-[62px]"
                      style={{ background: `${brand.teal}10` }}
                    />
                  ))
                : weatherData.map((w) => {
                    const Icon = weatherIcon(w.icon);
                    const color = weatherColor(w.icon);
                    return (
                      <motion.div
                        key={w.city}
                        variants={fadeUp}
                        className="flex items-center gap-3 rounded-xl border border-[var(--glass-edge)] glass p-3.5"
                      >
                        <Icon size={20} color={color} className="shrink-0" />
                        <div className="min-w-0">
                          <div className="text-[11px] tracking-[0.05em] text-ink-mute">{w.city}</div>
                          <div className="flex items-baseline gap-1.5">
                            <span className="font-display text-xl font-bold text-ink">{`${Math.round(w.tempC)}°C`}</span>
                            <span className="truncate text-[11px] text-ink-dim">{w.description}</span>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
            </motion.div>
          </div>

          {/* Right — events feed */}
          <div>
            <div className="mb-4 text-[11px] font-bold uppercase tracking-[0.2em] text-ink-dim">
              This week, in the valley
            </div>
            <motion.ul
              initial="hidden"
              whileInView="visible"
              viewport={inViewOnce}
              variants={staggerContainer}
              className="flex flex-col"
            >
              {events.length === 0
                ? Array.from({ length: 4 }).map((_, i) => (
                    <li
                      key={i}
                      className={
                        'flex items-center gap-3 py-3.5' +
                        (i < 3 ? ' border-b border-[var(--glass-edge)]' : '')
                      }
                    >
                      <span className="animate-pulse h-9 w-9 shrink-0 rounded-[10px] bg-[var(--glass-edge)]" />
                      <span className="flex-1 space-y-1.5">
                        <span className="animate-pulse block h-3 w-3/4 rounded bg-[var(--glass-edge)]" />
                        <span className="animate-pulse block h-2.5 w-1/3 rounded bg-[var(--glass-edge)]" />
                      </span>
                    </li>
                  ))
                : events.map((e, i) => {
                    const Icon = eventIcon(e.iconName);
                    const color = eventColor(e.colorToken);
                    return (
                      <motion.li
                        key={e.id}
                        variants={fadeUp}
                        className={
                          'flex items-center gap-3 py-3.5' +
                          (i < events.length - 1 ? ' border-b border-[var(--glass-edge)]' : '')
                        }
                      >
                        <span
                          className="grid h-9 w-9 shrink-0 place-items-center rounded-[10px]"
                          style={{ background: `${color}22`, border: `1px solid ${color}40` }}
                        >
                          <Icon size={16} color={color} />
                        </span>
                        <span className="flex-1">
                          <span className="block text-[13px] font-semibold text-ink">{e.title}</span>
                          <span className="block text-[11px] text-ink-mute">{e.datesText}</span>
                        </span>
                      </motion.li>
                    );
                  })}
            </motion.ul>
          </div>
        </div>
      </GlassCard>
    </section>
  );
}
